import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../hooks/useAuthCompat";
import { useApi, publicApi } from "../hooks/useApi";
import { toast } from "sonner";
import { Lock, ShieldCheck, X } from "lucide-react";

export default function Mods() {
  const api = useApi();
  const { isSignedIn } = useUser();
  const [tab, setTab] = useState("modules");
  const [checkingOut, setCheckingOut] = useState(false);
  const [openItem, setOpenItem] = useState(null);

  const { data: membership } = useQuery({ queryKey: ["mods-membership"], queryFn: async () => (await api.get("/mods/membership")).data, enabled: !!isSignedIn });
  const { data: modules } = useQuery({ queryKey: ["mods-modules"], queryFn: async () => (await publicApi.get("/mods/modules")).data });
  const { data: directives } = useQuery({ queryKey: ["mods-directives"], queryFn: async () => (await publicApi.get("/mods/directives")).data });
  const { data: offerStatus } = useQuery({ queryKey: ["mods-offer-status"], queryFn: async () => (await publicApi.get("/billing/offer-status/mods_monthly")).data });

  const unlocked = membership?.unlocked;
  const checkoutOpen = !!offerStatus?.checkout_enabled;
  const items = tab === "modules" ? modules : directives;

  const subscribe = async () => {
    if (!isSignedIn) return toast.error("Sign in to request MODS access.");
    setCheckingOut(true);
    try {
      const res = await api.post("/billing/checkout", { offer_key: "mods_monthly", origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
    } finally {
      setCheckingOut(false);
    }
  };

  const openEntry = async (item) => {
    if (!unlocked) return toast.error("MODS membership required — subscribe to unlock.");
    try {
      const path = tab === "modules" ? "modules" : "directives";
      const res = await api.get(`/mods/${path}/${item.id}`);
      setOpenItem(res.data);
    } catch {
      toast.error("Unable to open this resource.");
    }
  };

  return (
    <div className="theme-gold min-h-screen" data-testid="mods-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-16">
        <p className="text-xs uppercase tracking-[0.2em] text-textSecondary mb-2">MODS</p>
        <h1 className="font-display text-display-md md:text-display-lg font-medium mb-4">Executive Operator Modules</h1>
        <p className="text-textSecondary max-w-2xl mb-10">The highest tier of the Endure Beyond ecosystem — direct-access modules and field directives for operators rebuilding their cognitive, physiological, and execution architecture.</p>

        <div className="bg-card border border-primary/40 p-8 mb-14 max-w-xl" data-testid="mods-membership-card">
          {unlocked ? (
            <span className="inline-block text-xs uppercase tracking-wide text-primary border border-primary px-3 py-1.5">Active MODS Membership</span>
          ) : (
            <>
              <p className="text-3xl font-subtitle text-primary mb-4 font-meta">$10,000<span className="text-sm text-textSecondary font-body">/mo</span></p>
              {checkoutOpen ? (
                <>
                  <button data-testid="mods-subscribe-button" onClick={subscribe} disabled={checkingOut}
                    className="bg-primary text-primary-foreground px-6 py-3 text-sm uppercase tracking-wide hover:opacity-90 transition-opacity duration-200 disabled:opacity-50">
                    {checkingOut ? "Redirecting…" : "Request Access"}
                  </button>
                  <p data-testid="mods-secure-checkout-badge" className="text-xs text-textSecondary flex items-center gap-1.5 mt-4">
                    <ShieldCheck className="w-3.5 h-3.5 text-primary" /> Secure checkout ✓ — handled directly by Stripe
                  </p>
                </>
              ) : (
                <p data-testid="mods-checkout-closed-notice" className="text-xs uppercase tracking-wide text-textSecondary flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" /> Invite-only right now — not open for public checkout
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex gap-6 border-b border-border mb-8">
          <button data-testid="mods-tab-modules" onClick={() => setTab("modules")} className={`pb-3 text-sm uppercase tracking-wide ${tab === "modules" ? "text-primary border-b-2 border-primary" : "text-textSecondary"}`}>Modules</button>
          <button data-testid="mods-tab-directives" onClick={() => setTab("directives")} className={`pb-3 text-sm uppercase tracking-wide ${tab === "directives" ? "text-primary border-b-2 border-primary" : "text-textSecondary"}`}>Directives</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {items?.map((it) => (
            <button key={it.id} onClick={() => openEntry(it)} data-testid={`mods-item-card-${it.id}`}
              className="text-left bg-card border border-border p-5 hover:border-primary/50 transition-colors duration-200">
              <p className="text-xs uppercase tracking-wide text-textSecondary mb-2">{it.access_tier.replace("_", " ")}</p>
              <p className="text-sm mb-3">{it.title}</p>
              {unlocked ? (
                <span className="text-xs text-primary">Unlocked — open</span>
              ) : (
                <span className="text-xs text-textSecondary flex items-center gap-1"><Lock className="w-3 h-3" /> Members only</span>
              )}
            </button>
          ))}
          {items?.length === 0 && <p className="text-textSecondary text-sm">No items yet.</p>}
        </div>

        {openItem && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-6" onClick={() => setOpenItem(null)}>
            <div onClick={(e) => e.stopPropagation()} data-testid="mods-item-modal" className="relative bg-card border border-border p-8 max-w-2xl max-h-[80vh] overflow-y-auto">
              <button data-testid="mods-item-modal-close-icon" onClick={() => setOpenItem(null)} aria-label="Close" className="absolute top-4 right-4 text-textSecondary hover:text-white">
                <X className="w-5 h-5" />
              </button>
              <p className="text-xs uppercase tracking-wide text-textSecondary mb-2">{openItem.access_tier.replace("_", " ")}</p>
              <h3 className="text-xl font-subtitle mb-4 pr-8">{openItem.title}</h3>
              <p className="text-sm text-textSecondary whitespace-pre-line">{openItem.body}</p>
              {openItem.action_steps?.length > 0 && (
                <div className="mt-6" data-testid="mods-item-action-steps">
                  <p className="text-xs uppercase tracking-wide text-primary mb-2">Action Steps</p>
                  <ul className="list-disc list-inside text-sm text-textSecondary space-y-1">
                    {openItem.action_steps.map((step, i) => <li key={i}>{step}</li>)}
                  </ul>
                </div>
              )}
              {openItem.checklist?.length > 0 && (
                <div className="mt-6" data-testid="mods-item-checklist">
                  <p className="text-xs uppercase tracking-wide text-primary mb-2">Checklist</p>
                  <ul className="list-disc list-inside text-sm text-textSecondary space-y-1">
                    {openItem.checklist.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                </div>
              )}
              <button data-testid="mods-item-modal-close" onClick={() => setOpenItem(null)} className="mt-6 text-xs uppercase text-primary">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
